import { RefObject, useEffect } from "react";

type OutsideEvent = MouseEvent | TouchEvent;

// Every currently-open portaled panel using this hook registers its DOM node here, in the order
// it opened. A panel opened *while* another is already open (e.g. a DatePicker calendar rendered
// inside a HeaderMenu column filter) is necessarily a later entry - even though it portals
// separately to document.body as a sibling, not a DOM descendant, of the outer panel, so the outer
// panel's own `menu.contains(target)` check can't see it. We treat a click inside any
// later-opened panel as "inside" too, so an inner popup (calendar, month/year picker, ...) doesn't
// cause the panel it's nested in to close itself out from under it.
const openPanels: HTMLElement[] = [];

export function useOnClickOutsidePortal<T extends HTMLElement = HTMLElement>(
  menuRef: RefObject<T | null>,
  anchorEl: HTMLElement | null,
  onOutside: (e: OutsideEvent) => void,
  enabled: boolean = true
) {
  useEffect(() => {
    if (!enabled) return;

    const menu = menuRef.current;
    if (menu) {
      openPanels.push(menu);
    }

    const listener = (e: OutsideEvent) => {
      const target = e.target as Node | null;

      if (!target || !menu) return;

      // Click inside the menu (portaled)
      if (menu.contains(target)) return;

      // Click on the anchor (th / button) should NOT count as outside
      if (anchorEl && anchorEl.contains(target)) return;

      // Click inside a panel that opened after this one (nested inside it, e.g. a DatePicker
      // popup opened from within this menu) should NOT count as outside either
      const ownIndex = openPanels.indexOf(menu);
      const isInsideLaterPanel = openPanels.some(
        (panel, index) => index > ownIndex && panel !== menu && panel.contains(target),
      );
      if (isInsideLaterPanel) return;

      onOutside(e);
    };

    // capture phase is important when other handlers stopPropagation
    document.addEventListener("mousedown", listener, true);
    document.addEventListener("touchstart", listener, true);

    return () => {
      document.removeEventListener("mousedown", listener, true);
      document.removeEventListener("touchstart", listener, true);

      if (menu) {
        const index = openPanels.indexOf(menu);
        if (index !== -1) openPanels.splice(index, 1);
      }
    };
  }, [menuRef, anchorEl, onOutside, enabled]);
}
